import { Badge, type BadgeProps, Box } from "@mui/material";
import { debounce } from "lodash-es";
import { type FC, type SyntheticEvent, useRef, useState } from "react";

export interface HoverBadgeProps extends Omit<BadgeProps, "onSelect"> {
  name: string;
  editor: string;
  onSelect: (editor: string) => void;
  allowClickPropagation?: boolean;
}

export const HoverBadge: FC<HoverBadgeProps> = ({
  badgeContent,
  name,
  editor,
  onSelect,
  allowClickPropagation = false,
  children,
}) => {
  const [invisible, setInvisible] = useState(true);

  const selected = name === editor;
  const handleSelect = (e: SyntheticEvent): void => {
    if (!allowClickPropagation) e.stopPropagation();
    onSelect(name);
  };
  const ref = useRef<HTMLDivElement>(null);

  const debouncedSetInvisible = debounce(setInvisible, 200);

  return (
    <Badge
      color="secondary"
      variant="standard"
      invisible={invisible}
      ref={ref}
      onMouseOver={(e) => {
        e.stopPropagation();
        ref.current?.toggleAttribute("aria-active", true);
        debouncedSetInvisible(false);
      }}
      onMouseOut={(e) => {
        e.stopPropagation();
        debouncedSetInvisible(true);
      }}
      {...(selected && { "data-active": true })}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        border: "0px solid transparent",
        borderRadius: `${theme.shape.borderRadius}px`,
        cursor: "pointer",
      })}
      onClick={handleSelect}
      badgeContent={badgeContent}
    >
      {selected && (
        <Box
          sx={{
            borderRadius: "5px",
            border: "1px dashed #aaa",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}
      {!invisible && !selected && (
        <Box
          sx={{
            borderRadius: "5px",
            border: "1px dashed #aaa",
            backgroundColor: "rgba(0,0,0,0.05)",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />
      )}
      <Box sx={{ position: "relative" }}>{children}</Box>
    </Badge>
  );
};
